import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { adminAlertsService, AlertLogEntry, AlertType } from '@/services/adminAlertsService';

interface AlertLogTabProps {
  types: AlertType[];
}

const formatTime = (value: string) => {
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? value
    : d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export const AlertLogTab: React.FC<AlertLogTabProps> = ({ types }) => {
  const [entries, setEntries] = useState<AlertLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [alertType, setAlertType] = useState('');
  const [status, setStatus] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setEntries(await adminAlertsService.getLog({ limit: 100, alertType, status }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load sent alerts');
    } finally {
      setLoading(false);
    }
  }, [alertType, status]);

  useEffect(() => {
    load();
  }, [load]);

  const typeName = (key: string) => types.find(t => t.key === key)?.name || key;

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel id="log-event-label">Event</InputLabel>
          <Select labelId="log-event-label" label="Event" value={alertType} onChange={e => setAlertType(e.target.value)}>
            <MenuItem value="">All events</MenuItem>
            {types.map(t => <MenuItem key={t.key} value={t.key}>{t.name}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="log-status-label">Status</InputLabel>
          <Select labelId="log-status-label" label="Status" value={status} onChange={e => setStatus(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="sent">Sent</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
          </Select>
        </FormControl>
        <Button startIcon={<Refresh />} onClick={load} disabled={loading}>Refresh</Button>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
          Latest 100 alerts
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Time</TableCell>
              <TableCell>Event</TableCell>
              <TableCell>Rule</TableCell>
              <TableCell>Channel</TableCell>
              <TableCell>Recipient</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell>
              </TableRow>
            ) : entries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">No alerts sent yet</Typography>
                </TableCell>
              </TableRow>
            ) : (
              entries.map(entry => (
                <TableRow key={entry.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatTime(entry.sentAt)}</TableCell>
                  <TableCell>{entry.alertTypeName || typeName(entry.alertType)}</TableCell>
                  <TableCell>{entry.ruleName || <Typography variant="caption" color="text.secondary">Deleted rule</Typography>}</TableCell>
                  <TableCell>{entry.channel === 'sms' ? 'SMS' : 'Email'}</TableCell>
                  <TableCell sx={{ wordBreak: 'break-all' }}>{entry.recipient}</TableCell>
                  <TableCell>
                    {entry.status === 'failed' ? (
                      <Tooltip title={entry.error || 'No error details'}>
                        <Chip label="Failed" color="error" size="small" />
                      </Tooltip>
                    ) : (
                      <Chip label="Sent" color="success" size="small" variant="outlined" />
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default AlertLogTab;
